import { Header } from '@/components/Header'
import { type Navigation, type HeaderState } from '@/components/Header/Header'
import SPHeader from '@/components/Header/SPHeader'
import { PageTitleMap } from '@/components/Layout/util'
import { Block } from '@/components/base/Block'
import { IconButton } from '@/components/base/Button'
import { LocalIconType } from '@/components/base/Icon'
import { AppContext, useApp } from '@/hooks/useApp'
import { useAuth } from '@/hooks/useAuth'
import { useJobPostingAdvancedSearchConditionBackup } from '@/hooks/useJobPostingAdvancedSearchConditionBackup'
import useIsMobile from '@/hooks/useMobile'
import { useNavigator } from '@/hooks/useNavigator'
import { AppPath } from '@/routes/routes'
import { fontSize } from '@/styles/font'
import {
  V1IconType,
  V1NavigateActionType,
  V1RouteType,
  V1ScreenType
} from 'mnvm-mypage-app-ts-if'
import { memo, useContext, useEffect, useState } from 'react'
import { matchPath, useLocation } from 'react-router-dom'

const HeaderContainer = (): JSX.Element => {
  const app = useApp()
  const location = useLocation()
  const [showMenuHeader, setShowMenuHeader] = useState(true)
  const isMobile = useIsMobile()

  useEffect(() => {
    if (
      matchPath(
        { path: AppPath.JobPostingOfferAcceptance },
        location.pathname
      ) != null
    ) {
      setShowMenuHeader(false)
    } else {
      switch (location.pathname) {
        case AppPath.Profile:
        case AppPath.JobPostingSearchConditions:
        case AppPath.ChangePassword:
        case AppPath.ForgotPassword:
        case AppPath.ForgotPasswordComplete:
        case AppPath.ConfirmForgotPassword:
        case AppPath.ConfirmForgotPasswordComplete:
        case AppPath.SignUp:
        case AppPath.SignUpComplete:
        case AppPath.SignIn:
        case AppPath.JobSeekerSchedule:
        case AppPath.ChangeID:
        case AppPath.ChangeIDRequested:
        case AppPath.ChangeIDConfirm:
        case AppPath.Setting:
        case AppPath.Entry:
        case AppPath.Privacy:
        case AppPath.Term:
        case AppPath.JobSeekerMessageList:
          setShowMenuHeader(false)
          app.screenTitle = PageTitleMap[location.pathname]
          break
        default: {
          const isJobSeekerPreview =
            matchPath(
              { path: AppPath.JobSeekerSchedulePreview },
              location.pathname
            ) != null
          if (isJobSeekerPreview) {
            setShowMenuHeader(false)
            app.screenTitle = PageTitleMap[AppPath.JobSeekerSchedule]
            break
          }
          setShowMenuHeader(true)
        }
      }
    }

    return () => {}
  }, [location.pathname])

  return (
    <Block
      style={{
        position: 'sticky',
        top: 0,
        backgroundColor: 'white',
        zIndex: 11
      }}
    >
      {isMobile ? (
        <Block>
          {showMenuHeader ? (
            <MenuHeaderContainer />
          ) : (
            <SPHeaderContainer title={app.screenTitle} />
          )}
        </Block>
      ) : (
        <Block>
          <MenuHeaderContainer />
        </Block>
      )}
    </Block>
  )
}

interface SPHeaderContainerProps {
  title: string
}

const SPHeaderContainer = (props: SPHeaderContainerProps): JSX.Element => {
  const app = useApp()
  const nav = useNavigator()
  const [showBackButton, setShowBackButton] = useState(true)

  useEffect(() => {
    switch (location.pathname) {
      case AppPath.Entry:
      case AppPath.Privacy:
      case AppPath.Term:
        setShowBackButton(false)
        break
      default:
        setShowBackButton(true)
    }

    return () => {}
  }, [location.pathname])

  return (
    <SPHeader
      title={props.title}
      backgroundColor={app.headerBackroundColor}
      leading={
        showBackButton ? (
          <IconButton
            icon={LocalIconType.ArrowBackIos}
            color={app.theme.mainTextColor}
            fontSize={fontSize.XXL}
            onClick={() => {
              void nav?.goBack()
            }}
          />
        ) : (
          <Block height="50px" />
        )
      }
    />
  )
}

const MenuHeaderContainer = (): JSX.Element => {
  const app = useApp()
  const nav = useNavigator()
  const [, updateJobPostingAdvancedSearchConditionBackup] =
    useJobPostingAdvancedSearchConditionBackup()
  const { isAuthenticated } = useAuth()
  const { dispatch } = useContext(AppContext)

  const iconImage = app.iconImage
  const navigationListPC =
    app.pcHeaderMenu.items?.map((item): Navigation => {
      return {
        name: item.text ?? '',
        icon: item.iconType ?? V1IconType.Unspecified,
        onClick: () => {
          if (
            item.navigateActionType === V1NavigateActionType.GotoSpecificRoute
          ) {
            if (V1RouteType.JobPostingAdvancedSearch === item.routeType) {
              updateJobPostingAdvancedSearchConditionBackup(undefined)
            }
            if (V1RouteType.FavoriteJobPostings === item.routeType) {
              if (item.iconType === V1IconType.History) {
                void nav?.gotoFavoriteJobposting(1)
                dispatch({
                  ...app,
                  screenType: V1ScreenType.JobPostingBrowsingHistory
                })
              } else {
                void nav?.gotoFavoriteJobposting(0)
                dispatch({
                  ...app,
                  screenType: V1ScreenType.FavoriteJobPostings
                })
              }

              return
            }
            if (V1RouteType.JobPostingJobSeekerAction === item.routeType) {
              void nav?.gotoFavoriteJobposting(2)
              dispatch({
                ...app,
                screenType: V1ScreenType.JobPostingInquired
              })
              return
            }

            if (V1RouteType.Url === item.routeType) {
              window.open(item.url ?? '', '_blank')
              return
            }

            void nav?.goToRouteByRouteType(item.routeType)
          }
        }
      }
    }) ?? []

  const navigationListSP =
    app.hamburgerMenu.items?.map((item): Navigation => {
      return {
        name: item.text ?? '',
        icon: item.iconType ?? V1IconType.Unspecified,
        onClick: () => {
          if (
            item.navigateActionType === V1NavigateActionType.GotoSpecificRoute
          ) {
            if (V1RouteType.FavoriteJobPostings === item.routeType) {
              if (item.iconType === V1IconType.History) {
                void nav?.gotoFavoriteJobposting(1)
                dispatch({
                  ...app,
                  screenType: V1ScreenType.JobPostingBrowsingHistory
                })
              } else {
                void nav?.gotoFavoriteJobposting(0)
                dispatch({
                  ...app,
                  screenType: V1ScreenType.FavoriteJobPostings
                })
              }

              return
            }
            if (V1RouteType.JobPostingJobSeekerAction === item.routeType) {
              void nav?.gotoFavoriteJobposting(2)
              dispatch({
                ...app,
                screenType: V1ScreenType.JobPostingInquired
              })
              return
            }

            if (V1RouteType.Url === item.routeType) {
              window.open(item.url ?? '', '_blank')
              return
            }

            void nav?.goToRouteByRouteType(item.routeType)
          }
        }
      }
    }) ?? []

  const [headerState, setHeaderState] = useState<HeaderState>({
    isNavigationListOpen: false
  })

  const onClickOpenNavigationList = (): void => {
    setHeaderState({
      isNavigationListOpen: true
    })
  }
  const onClickCloseNavigationList = (): void => {
    setHeaderState({
      isNavigationListOpen: false
    })
  }

  const getNavigationListForPC = (): Navigation[] => {
    const isHiddenNavigationList =
      [AppPath.JobSeekerSchedulePreview].filter((e, index) => {
        return matchPath({ path: e }, location.pathname)
      }).length > 0
    if (isHiddenNavigationList) return []

    return isAuthenticated ? navigationListPC : []
  }

  const jobPostingDetailMatch = matchPath(
    { path: AppPath.JobPostingDetail, end: true },
    location.pathname
  )

  // match only digit for param id
  const isJobPostingDetail =
    jobPostingDetailMatch != null &&
    typeof jobPostingDetailMatch.params?.id === 'string' &&
    /^\d+$/.test(jobPostingDetailMatch.params.id)

  return (
    <Block borderBottom>
      <Header
        headerState={headerState}
        iconImage={iconImage}
        navigationListPC={getNavigationListForPC()}
        navigationListSP={navigationListSP}
        onClickOpenNavigationList={onClickOpenNavigationList}
        onClickCloseNavigationList={onClickCloseNavigationList}
        isJobPostingDetail={isJobPostingDetail}
      />
    </Block>
  )
}

export default memo(HeaderContainer)
